/* eslint-disable func-names */
import { composeFullname } from '../lib/DisplayName';
import { formatDateTime, TRIAL_END_DATE_FORMAT } from '../lib/Date';
import { INTERCOM_APP_ID, INTERCOM_IS_ENABLED } from '../config/env';
import {
  UserPendingActionsStore,
  UserRolesStore,
} from '../stores/observables/UserStore';
import { UserSchema } from '../definitions/schemas';
import OrganizationsStore from '../stores/observables/OrganizationsStore';

declare global {
  interface Window {
    intercomSettings: any;
    Intercom: any;
  }
}

const getIntercomBaseOptions = () => ({
  app_id: INTERCOM_APP_ID,
  alignment: 'right',
});

// @todo use an npm loader for Intercom
const loadIntercom = (appId: string, callback: () => void) => {
  const w = window;
  const ic = w.Intercom;
  if (typeof ic === 'function') {
    ic('reattach_activator');
    ic('update', w.intercomSettings);
  } else {
    const d = document;
    const i: any = function (...args: any[]) {
      i.c(args);
    };
    i.q = [];
    i.c = function (args: any) {
      i.q.push(args);
    };
    w.Intercom = i;
    const l = function () {
      const s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = `https://widget.intercom.io/widget/${appId}`;
      const x = d.getElementsByTagName('script')[0];
      s.onload = () => {
        if (callback) callback();
      };
      x.parentNode?.insertBefore(s, x);
    };
    if (document.readyState === 'complete') {
      l();
    } else if ((w as any).attachEvent) {
      (w as any).attachEvent('onload', l);
    } else {
      w.addEventListener('load', l, false);
    }
  }
};

class Messenger {
  private isIntercom: boolean;

  private isIntercomRegistered: boolean;

  private retryTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.isIntercom = false;
    this.isIntercomRegistered = false;
  }

  /**
   * Intercom identify
   * @see https://developers.intercom.com/installing-intercom/docs/enable-identity-verification-on-your-web-product
   */
  public identify(user: UserSchema, isExtensionInstalled: boolean) {
    if (!this.isIntercomRegistered) {
      return;
    }

    if (!this.isIntercom) {
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
      }

      /**
       * Retrying to identify if the Intercom script hasn't been loaded yet
       * this could happen because of a race condition, where the app tries
       * to identify the messenger before actually loading its dependencies
       */
      setTimeout(() => this.identify(user, isExtensionInstalled), 2500);
      return;
    }

    const { changePassword } = UserPendingActionsStore.getCurrent();

    /**
     * @see https://developers.intercom.com/installing-intercom/docs/javascript-api-attributes-objects#data-attributes
     */
    let options: Record<string, any> = {
      ...getIntercomBaseOptions(),
      user_id: user.id,
      email: user.email,
      first_name: user.firstName,
      has_set_password: !changePassword,
      last_name: user.lastName,
      name: composeFullname(user),
      phone: user.phoneNumber,
    };

    // write to this field only when there is an existing marketo lead that matches this user
    if (user.marketoId) {
      options = {
        ...options,
        unsubscribed_from_emails: !user.subscribedToMarketing,
      };
    }

    // Organization context is not always available
    const currentOrganization = OrganizationsStore.getCurrentOrganization();
    if (currentOrganization) {
      const {
        activeUsersCount,
        createdAt,
        discoveryReason,
        employeeRange,
        id,
        name,
        primaryUsage,
        status,
        orgSubscription,
        subscriptionStatus,
        totalUsersCount,
        vendorId,
        website,
      } = currentOrganization;

      options = {
        ...options,
        organization_id: id,
        organization_name: name,
        organization_subscription_status: subscriptionStatus,
        // date formatted in the user's timezone, used for display purposes
        trial_end_date_label: orgSubscription?.trialEndDate
          ? formatDateTime(orgSubscription?.trialEndDate, TRIAL_END_DATE_FORMAT)
          : null,

        /**
         * @see https://developers.intercom.com/installing-intercom/docs/javascript-api-attributes-objects#company-object
         */
        company: {
          'active users': activeUsersCount,
          'discovery reason': discoveryReason,
          'employee range': employeeRange,
          'organization status': status,
          'organization subscription status': orgSubscription?.status,
          'specified goal': primaryUsage,
          'total users': totalUsersCount,
          'trial end date': orgSubscription?.trialEndDate,
          'trial start date': orgSubscription?.createdAt,
          'vendor id': vendorId,
          created_at: createdAt,
          id,
          name,
          plan: orgSubscription?.plan?.name,
          size: orgSubscription?.licenses,
          website,
        },
      };
    }

    // Role context is not always available
    const role = UserRolesStore.getCurrent()?.role.name;
    if (role) {
      options = { ...options, role };
    }

    /**
     * Sending the chrome extension installed flag if and only if the flag is
     * true, as we're interested to know this once and not every time. By sending
     * the value in false just once, it would overwrite the user's attribute,
     * so we only send it when we're sure the user has installed and logged into it
     */
    if (isExtensionInstalled) {
      options = {
        ...options,
        chrome_extension_installed: isExtensionInstalled,
      };
    }

    window.Intercom('boot', options);
  }

  public anonymize() {
    if (!this.isIntercom) {
      return;
    }
    console.info(`Intercom anonymized`);
    window.Intercom('shutdown');
  }

  public registerIntercom() {
    if (!INTERCOM_APP_ID || !INTERCOM_IS_ENABLED) {
      return;
    }
    this.isIntercomRegistered = true;
    console.info(`Intercom Initialized: ${INTERCOM_APP_ID}`);
    loadIntercom(INTERCOM_APP_ID, () => {
      this.isIntercom = true;
    });
  }

  // Register all known services
  public register() {
    this.registerIntercom();
  }

  public startConversation(message: string) {
    if (!this.isIntercom) {
      return;
    }

    window.Intercom('showNewMessage', message);
  }
}

// Export a singleton instance
const inst = new Messenger();
export default inst;
